import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TextInput } from "flowbite-react";
import * as Joi from "joi";
import { v4 as uuidv4 } from "uuid";
import {
  BiEnvelope,
  BiLinkExternal,
  BiPhone,
  BiPlus,
  BiSave,
} from "react-icons/bi";
import { FaWhatsapp } from "react-icons/fa";
import { HiOutlineTrash } from "react-icons/hi";
import { CustomInput } from "./CustomInput";
import { FieldLabel } from "./FieldLabel";
import { CharacterCounter } from "./CharacterCounter";
import { IconPicker, SuggestedIcon } from "./IconPicker";
import {
  extractValueFromDeepLink,
  validateLinks,
} from "../../utils/linkValidation";
import { extractProtocol } from "../../utils/deepLinkHelpers";
import {
  CommunicationTemplate,
  DEEP_LINK_PROTOCOLS,
  COMMUNICATION_ICON_IDS,
  findTemplateByProtocol,
  useCommunicationTemplates,
} from "../../utils/communicationTemplates";
import { CustomButton } from "./CustomButton";

export interface LinkItem {
  id: string;
  label: string;
  link: string;
  fallbackLink?: string;
  icontype?: "icon" | "url";
  icon?: string;
}

export interface LinkManagerProps {
  data?: LinkItem[];
  onSubmit: (data: { links: LinkItem[] }) => Promise<void>;
  maxLinks: number;
  allowHttp?: boolean;
  autoSave?: boolean;
  className?: string;
  showQuickTemplates?: boolean;
  translations?: {
    linkTitle?: string;
    addButton?: string;
    saveButton?: string;
    titleRequired?: string;
    linkRequired?: string;
    linkInvalid?: string;
    iconTitle?: string;
  };
  labelPlaceholder?: string;
  linkPlaceholder?: string;
  iconPlaceholder?: string;
  showIconField?: boolean;
  customIcons?: SuggestedIcon[];
  showCustomIconUrl?: boolean;
  canEdit?: boolean;
}

interface LinkFormData {
  links: LinkItem[];
}

const computeFallbackLink = (link: LinkItem): string => {
  const existingFallback = link.fallbackLink?.trim();
  if (existingFallback) {
    return existingFallback;
  }

  const rawLink = link.link?.trim();
  if (!rawLink) {
    return "";
  }

  const protocol = extractProtocol(rawLink);
  if (!protocol) {
    return "";
  }

  const template = findTemplateByProtocol(protocol);
  if (!template || !template.fallbackLink) {
    return "";
  }

  try {
    const extractedValue = extractValueFromDeepLink(rawLink, template);
    if (!extractedValue) {
      return "";
    }
    return template.fallbackLink(extractedValue).trim();
  } catch (error) {
    console.warn(`Failed to derive fallback link for ${rawLink}`, error);
    return "";
  }
};

const normalizeLinkItem = (link: LinkItem): LinkItem => ({
  ...link,
  icontype: link.icontype || undefined,
  icon: link.icon || "",
  fallbackLink: computeFallbackLink(link),
});

export const LinkManager = ({
  data = [],
  onSubmit,
  maxLinks,
  allowHttp = false,
  autoSave = false,
  className = "",
  showQuickTemplates = true,
  translations,
  labelPlaceholder = "Link title",
  linkPlaceholder = "https://",
  iconPlaceholder = "Select an icon (optional)",
  showIconField = false,
  customIcons = [],
  showCustomIconUrl = false,
  canEdit = true,
}: LinkManagerProps) => {
  const { t } = useTranslation();
  const communicationTemplates = useCommunicationTemplates();
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<CommunicationTemplate | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  const filteredCustomIcons = useMemo(
    () =>
      customIcons.filter((icon) => !COMMUNICATION_ICON_IDS.includes(icon.id)),
    [customIcons]
  );

  const defaultTranslations = useMemo(
    () => ({
      linkTitle:
        translations?.linkTitle ||
        t("dashboard.features.lmc.redirectLinks.linkTitle"),
      addButton:
        translations?.addButton ||
        t("dashboard.features.lmc.redirectLinks.addLinkButton"),
      saveButton: translations?.saveButton || t("common.saveShort"),
      titleRequired:
        translations?.titleRequired ||
        t("dashboard.features.lmc.redirectLinks.titleRequiredText"),
      linkRequired:
        translations?.linkRequired ||
        t("dashboard.features.lmc.redirectLinks.linkRequiredText"),
      linkInvalid:
        translations?.linkInvalid ||
        t("dashboard.features.lmc.redirectLinks.LinkValid"),
    }),
    [t, translations]
  );

  const allowedSchemes = useMemo(() => {
    const protocols = new Set<string>(["https", ...DEEP_LINK_PROTOCOLS]);
    if (allowHttp) {
      protocols.add("http");
    }
    return Array.from(protocols);
  }, [allowHttp]);

  const customResolver = useCallback(
    async (values: LinkFormData) => {
      const schema = Joi.object({
        links: Joi.array()
          .items(
            Joi.object({
              id: Joi.string().allow(""),
              label: Joi.string().required(),
              link: Joi.string().required(),
              fallbackLink: Joi.string().allow("").optional(),
              icontype: Joi.string().valid("icon", "url").optional(),
              icon: Joi.string().allow("").optional(),
            })
          )
          .max(maxLinks),
      });

      const { error } = schema.validate(values, { abortEarly: false });

      const errors: any = {};

      const validationErrors = validateLinks(
        values.links,
        communicationTemplates,
        allowedSchemes
      );

      validationErrors.forEach(({ index, field, error: errorMsg }) => {
        if (!errors.links) errors.links = [];
        if (!errors.links[index]) errors.links[index] = {};

        errors.links[index][field] = {
          message: errorMsg,
        };
      });

      if (error) {
        error.details.forEach((detail) => {
          const path = detail.path;
          if (path[0] === "links" && typeof path[1] === "number") {
            const index = path[1];
            const field = path[2];

            if (!errors.links) errors.links = [];
            if (!errors.links[index]) errors.links[index] = {};

            if (field === "label") {
              errors.links[index].label = {
                message: defaultTranslations.titleRequired,
              };
            } else if (field === "link") {
              errors.links[index].link = {
                message: defaultTranslations.linkRequired,
              };
            } else if (field === "icon") {
              errors.links[index].icon = {
                message: t("dashboard.features.lmc.redirectLinks.validIconUrl"),
              };
            }
          }
        });
      }

      return {
        values: Object.keys(errors).length === 0 ? values : {},
        errors,
      };
    },
    [allowedSchemes, maxLinks, defaultTranslations, t, communicationTemplates]
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, dirtyFields },
    reset,
    watch,
    setValue,
    trigger,
  } = useForm<LinkFormData>({
    resolver: customResolver,
    defaultValues: { links: [] },
    mode: "onChange",
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "links",
  });

  const watchedLinks = watch("links");

  useEffect(() => {
    const normalizedData = (data ?? []).map(normalizeLinkItem);

    reset({ links: normalizedData });
  }, [data, reset]);

  const handleFormSubmit = useCallback(
    async (formData: LinkFormData) => {
      try {
        const normalizedLinks = formData.links.map(normalizeLinkItem);
        const payload = { links: normalizedLinks };

        await onSubmit(payload);
        if (!autoSave) {
          reset(payload);
        }
      } catch (error) {
        console.error("Error submitting links:", error);
      }
    },
    [onSubmit, autoSave, reset]
  );

  const handleAddLink = useCallback(() => {
    append({
      id: uuidv4(),
      label: "",
      link: "",
      fallbackLink: "",
      icon: "",
    });
  }, [append]);

  const handleRemoveLink = useCallback(
    async (index: number) => {
      remove(index);
      const currentData = watchedLinks?.filter((_, i) => i !== index) || [];
      await handleFormSubmit({ links: currentData });
    },
    [remove, watchedLinks, handleFormSubmit]
  );

  const handleTemplateSelect = useCallback(
    (template: CommunicationTemplate, inputValue: string) => {
      if (!template.validator || template.validator(inputValue)) {
        const trimmed = inputValue.trim();
        const finalUrl = template.buildLink(trimmed);
        const fallbackLink = template.fallbackLink?.(trimmed) ?? "";
        append({
          id: uuidv4(),
          label: template.label,
          link: finalUrl,
          fallbackLink,
          icon: template.iconId,
          icontype: "icon",
        });
        setShowTemplateModal(false);
        setSelectedTemplate(null);
        setInputValue("");
        setInputError("");
      }
    },
    [append]
  );

  const handleTemplateClick = useCallback((template: CommunicationTemplate) => {
    setSelectedTemplate(template);
    setInputValue("");
    setInputError("");
  }, []);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (
        selectedTemplate?.validator &&
        !selectedTemplate.validator(value) &&
        value.length > 0
      ) {
        setInputError(
          t("dashboard.features.lmc.redirectLinks.validInputError", {
            field: selectedTemplate.inputLabel.toLowerCase(),
          }) as string
        );
      } else {
        setInputError("");
      }
    },
    [selectedTemplate, t]
  );

  const handleCreate = useCallback(() => {
    if (selectedTemplate && inputValue.trim()) {
      if (
        !selectedTemplate.validator ||
        selectedTemplate.validator(inputValue)
      ) {
        handleTemplateSelect(selectedTemplate, inputValue.trim());
        setSelectedTemplate(null);
        setInputValue("");
      }
    }
  }, [selectedTemplate, inputValue, handleTemplateSelect]);

  const canCreate = selectedTemplate && inputValue.trim() && !inputError;

  const QuickTemplateModal = () => {
    if (!showTemplateModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {t(
                  "dashboard.features.lmc.redirectLinks.quickCommunicationLinks"
                )}
              </h3>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                ✕
              </button>
            </div>
            <p className="text-sm text-gray-600 mt-1">
              {t(
                "dashboard.features.lmc.redirectLinks.chooseCommunicationMethod"
              )}
            </p>
          </div>

          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {!selectedTemplate ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {communicationTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateClick(template)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all text-center group"
                  >
                    <div className="flex justify-center mb-2 text-2xl">
                      {template.icon}
                    </div>
                    <div className="text-sm font-medium text-gray-900 mb-1">
                      {template.label}
                    </div>
                    <div className="text-xs text-gray-500">
                      {template.description}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
                >
                  ← {t("dashboard.features.lmc.redirectLinks.backToTemplates")}
                </button>

                <div className="text-center py-4">
                  <div className="flex justify-center mb-3 text-2xl">
                    {selectedTemplate.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">
                    {selectedTemplate.label}
                  </h4>
                  <p className="text-sm text-gray-600">
                    {selectedTemplate.description}
                  </p>
                </div>

                <div className="space-y-3">
                  <FieldLabel>{selectedTemplate.inputLabel}</FieldLabel>
                  <TextInput
                    type="text"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && canCreate) {
                        e.preventDefault();
                        handleCreate();
                      }
                    }}
                    placeholder={selectedTemplate.placeholder}
                    color={inputError ? "failure" : undefined}
                    className="w-full"
                    autoFocus
                  />
                  {inputError && (
                    <p className="text-red-600 text-xs">{inputError}</p>
                  )}
                </div>

                <div className="flex gap-2 pt-4">
                  <CustomButton
                    variant="secondary"
                    onClick={() => setSelectedTemplate(null)}
                    title={t("common.cancel")}
                    className="flex-1"
                  />
                  <CustomButton
                    variant="primary"
                    onClick={handleCreate}
                    disabled={!canCreate}
                    title={t("dashboard.features.lmc.redirectLinks.createLink")}
                    className="flex-1"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`w-full ${className}`} style={{ overflow: "visible" }}>
      <QuickTemplateModal />
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        {fields.length === 0 && (
          <div className="mb-6 p-8 bg-blue-50 border-2 border-dashed border-blue-200 rounded-xl text-center">
            <div className="flex flex-col items-center gap-3">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <BiLinkExternal size={32} className="text-blue-500" />
              </div>
              <div>
                <h4 className="text-gray-900 text-base font-bold mb-1">
                  {t("dashboard.features.lmc.redirectLinks.emptyStateTitle")}
                </h4>
                <p className="text-gray-600 text-sm">
                  {t(
                    "dashboard.features.lmc.redirectLinks.emptyStateDescription"
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-2 mt-2 text-xs text-gray-500 text-start">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span>
                    {t("dashboard.features.lmc.redirectLinks.emptyExample1")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span>
                    {t("dashboard.features.lmc.redirectLinks.emptyExample2")}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-blue-400 rounded-full"></span>
                  <span>
                    {t("dashboard.features.lmc.redirectLinks.emptyExample3")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {fields?.map((item, index) => {
          const linkDirty = dirtyFields?.links?.[index];
          const isLinkDirty =
            linkDirty?.label ||
            linkDirty?.link ||
            linkDirty?.icon ||
            linkDirty?.icontype;

          const linkValid =
            !errors.links?.[index]?.label &&
            !errors.links?.[index]?.link &&
            !errors.links?.[index]?.icon;

          return (
            <div
              key={item.id}
              className="mb-3 sm:mb-6 p-2.5 sm:p-4 bg-white border border-gray-200 rounded-lg shadow-sm"
            >
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-semibold text-blue-600">
                      {index + 1}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Link {index + 1}
                  </span>
                </div>
                {canEdit && (
                  <CustomButton
                    variant="none"
                    icon={<HiOutlineTrash size={18} />}
                    onClick={() => handleRemoveLink(index)}
                    title=""
                    size="icon"
                    tooltip={t("common.delete") as string}
                    className="rounded-md text-gray-400 transition-colors enabled:hover:text-red-600"
                  />
                )}
              </div>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    {t(
                      "dashboard.features.lmc.redirectLinks.whatShouldPeopleSee"
                    )}
                  </label>
                  <div
                    className={`grid gap-2 sm:gap-3 items-start ${
                      showIconField
                        ? "grid-cols-[auto_1fr] sm:grid-cols-[80px_1fr]"
                        : "grid-cols-1"
                    }`}
                  >
                    {showIconField && (
                      <div className="min-w-0">
                        <IconPicker
                          disabled={!canEdit}
                          selectedIcon={watchedLinks[index]?.icon || ""}
                          onIconSelect={async (iconId) => {
                            if (!iconId) {
                              setValue(`links.${index}.icontype`, undefined, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                              setValue(`links.${index}.icon`, "", {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            } else {
                              const isUrl =
                                iconId.startsWith("http://") ||
                                iconId.startsWith("https://");
                              setValue(
                                `links.${index}.icontype`,
                                isUrl ? "url" : "icon",
                                { shouldValidate: true, shouldDirty: true }
                              );
                              setValue(`links.${index}.icon`, iconId, {
                                shouldValidate: true,
                                shouldDirty: true,
                              });
                            }
                            await trigger([
                              `links.${index}.icontype`,
                              `links.${index}.icon`,
                            ]);
                          }}
                          customIcons={filteredCustomIcons}
                          showCustomUrlInput={showCustomIconUrl}
                          placeholder={iconPlaceholder}
                        />
                      </div>
                    )}
                    <div className="min-w-0">
                      <CustomInput
                        name={`links.${index}.label`}
                        control={control}
                        grayBackground
                        error={errors.links?.[index]?.label}
                        placeholder={labelPlaceholder}
                        className={`w-full text-gray-800 text-sm font-normal font-figtree leading-tight ${
                          errors.links?.[index]?.label
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : ""
                        }`}
                        maxLength={40}
                      />
                      <CharacterCounter
                        currentLength={watchedLinks[index]?.label?.length || 0}
                        maxLength={40}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:gap-2">
                  <div className="flex-1 min-w-0">
                    <CustomInput
                      name={`links.${index}.link`}
                      control={control}
                      grayBackground
                      error={errors.links?.[index]?.link}
                      type="url"
                      placeholder={linkPlaceholder}
                      className={`w-full text-gray-800 text-sm font-normal font-figtree leading-tight ${
                        errors.links?.[index]?.link
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                          : ""
                      }`}
                      label={
                        t(
                          "dashboard.features.lmc.redirectLinks.whereShouldItGo"
                        ) as string
                      }
                      maxLength={300}
                    />
                    <CharacterCounter
                      currentLength={watchedLinks[index]?.link?.length || 0}
                      maxLength={300}
                    />
                  </div>
                  {canEdit &&
                    !autoSave &&
                    isLinkDirty &&
                    (linkValid || isValid) && (
                      <div className="flex justify-end sm:block sm:self-start sm:mt-[22px]">
                        <CustomButton
                          type="submit"
                          variant="primary"
                          size="sm"
                          disabled={!isLinkDirty || !linkValid || !isValid}
                          tooltip={defaultTranslations.saveButton as string}
                          icon={<BiSave size={18} />}
                          title={
                            <span className="whitespace-nowrap">
                              {defaultTranslations.saveButton}
                            </span>
                          }
                        />
                      </div>
                    )}
                </div>
              </div>

              {(errors.links?.[index]?.label ||
                errors.links?.[index]?.link ||
                errors.links?.[index]?.icon) && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg space-y-2">
                  {errors.links[index]?.label && (
                    <div className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-red-700 text-sm font-medium">
                        {errors.links[index]?.label?.message}
                      </p>
                    </div>
                  )}
                  {errors.links[index]?.link && (
                    <div className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-red-700 text-sm font-medium">
                        {errors.links[index]?.link?.message}
                      </p>
                    </div>
                  )}
                  {errors.links[index]?.icon && (
                    <div className="flex items-start gap-2">
                      <svg
                        className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-red-700 text-sm font-medium">
                        {errors.links[index]?.icon?.message}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {canEdit && (watchedLinks?.length || 0) < maxLinks && (
          <div className="mb-4 space-y-3">
            {showQuickTemplates && (
              <button
                type="button"
                onClick={() => setShowTemplateModal(true)}
                className="w-full p-4 border-2 border-dashed border-green-300 rounded-lg bg-green-50 hover:border-green-400 hover:bg-green-100 transition-all group relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 opacity-10">
                  <svg
                    width="80"
                    height="80"
                    viewBox="0 0 100 100"
                    fill="currentColor"
                    className="text-green-600"
                  >
                    <path d="M50 15 L60 40 L85 45 L65 62 L70 87 L50 75 L30 87 L35 62 L15 45 L40 40 Z" />
                  </svg>
                </div>

                <div className="flex items-center justify-center gap-2 relative">
                  <div className="flex gap-1">
                    <FaWhatsapp className="text-green-600 text-lg" />
                    <BiEnvelope className="text-red-600 text-lg" />
                    <BiPhone className="text-blue-600 text-lg" />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 group-hover:text-green-800">
                    {t(
                      "dashboard.features.lmc.redirectLinks.quickCommunicationLinks"
                    )}
                  </span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-sm">
                    BETA
                  </span>
                </div>
                <p className="text-xs text-gray-700 mt-2 font-medium relative">
                  {t(
                    "dashboard.features.lmc.redirectLinks.quickCommunicationDescriptionImproved"
                  )}
                </p>
                <p className="text-xs text-green-700 mt-1 font-semibold relative">
                  💡{" "}
                  {t(
                    "dashboard.features.lmc.redirectLinks.quickCommunicationBenefit"
                  )}
                </p>
              </button>
            )}

            <button
              type="button"
              onClick={handleAddLink}
              className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 hover:border-blue-400 hover:bg-blue-50 transition-all group"
            >
              <div className="flex items-center justify-center gap-2">
                <BiPlus
                  size={20}
                  className="text-gray-400 group-hover:text-blue-500"
                />
                <span className="text-sm font-medium text-gray-600 group-hover:text-blue-600">
                  {t("dashboard.features.lmc.redirectLinks.customLink", {
                    current: watchedLinks?.length || 0,
                    max: maxLinks,
                  })}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {t("dashboard.features.lmc.redirectLinks.addCustomUrl")}
              </p>
            </button>
          </div>
        )}
      </form>
    </div>
  );
};
